/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import React from 'react';
import { Collapse } from 'antd';
import { BookOutlined, CaretRightOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import Loader, { ILoaderType } from '../Loader';
import LearningMethod from './LearningMethod/LearningMethod';
import {
  getQuestionInputTypeFromAnswerAction,
  getQuestionInputValueFromAction
} from '../../utils/request';
import {
  AnswerActionType,
  QuestionInputType,
  RequestQuestionType
} from './interface';
import {
  GetLearnedAnswerSuggestionsQuery,
  GetLearnedAnswerSuggestionsQueryVariables,
  LearnedAnswer,
  Procedure,
  Request,
  ReviewConceptQuestion,
  ValueNotKnownQuestion
} from '../../generated/graphql';
import { LIST_LEARNED_ANSWER_SUGGESTIONS } from '../../graphql/queries/learnings';
import useFeatureFlag from '../../hooks/useFeatureFlag';
import useDepartment from '../../hooks/useDepartment';
import styles from './RequestLearningSuggestions.module.less';

interface IRequestLearningSuggestionsProps {
  request: Request;
  question: RequestQuestionType;
  procedure?: Procedure;
  answerId?: string;
  onSuggestionClick: (learningSuggestion: LearnedAnswer) => void;
}

function RequestLearningSuggestions(props: IRequestLearningSuggestionsProps) {
  const { request, question, procedure, answerId, onSuggestionClick } = props;

  const { department } = useDepartment();
  const [{ rolloutContextBasedLearning }] = useFeatureFlag({
    context: { department: { key: department!.id, dept_id: department!.id } }
  });

  const valueNotKnownQuestion = question as unknown as ValueNotKnownQuestion;
  const conceptReviewQuestion = question as unknown as ReviewConceptQuestion;

  const lexicalPath = (valueNotKnownQuestion || conceptReviewQuestion)
    .lexicalPath;

  const {
    data: learnedAnswerSuggestions,
    loading: learnedAnswerSuggestionsLoading
  } = useQuery<
    GetLearnedAnswerSuggestionsQuery,
    GetLearnedAnswerSuggestionsQueryVariables
  >(LIST_LEARNED_ANSWER_SUGGESTIONS, {
    variables: {
      input: {
        requestId: request?.id,
        departmentId: department?.id!,
        procedureId: procedure?.id!,
        lexicalPath,
        stepPath: request?.stepPath
      }
    },
    skip: !!answerId || !procedure || !rolloutContextBasedLearning
  });

  const learningSuggestions = (learnedAnswerSuggestions
    ?.getLearnedAnswerSuggestions?.items || []) as LearnedAnswer[];

  if (learnedAnswerSuggestionsLoading) {
    return <Loader type={ILoaderType.SKELETON} />;
  }

  if (learningSuggestions.length === 0) {
    return null;
  }

  return (
    <div className={styles.learningSuggestions} data-cy="available-learnings">
      <Collapse
        bordered={false}
        ghost
        expandIconPosition="end"
        // eslint-disable-next-line react/no-unstable-nested-components
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
      >
        <Collapse.Panel
          header={
            <div>
              <div className={styles.header}>
                <div className={styles.title}>
                  <BookOutlined />
                  <span>Available learnings: {learningSuggestions.length}</span>
                </div>
              </div>
            </div>
          }
          key="1"
        >
          <>
            {learningSuggestions!.map((learningSuggestion, index) => {
              const answerText = getQuestionInputValueFromAction(
                learningSuggestion.action as AnswerActionType,
                true
              );
              const inputType = getQuestionInputTypeFromAnswerAction(
                learningSuggestion.action!
              );

              return (
                <LearningMethod
                  key={learningSuggestion.id}
                  header={`Learning ${index + 1}`}
                  body={answerText}
                  showTryNow={inputType === QuestionInputType.TECHNIQUE}
                  onClick={() => onSuggestionClick(learningSuggestion)}
                />
              );
            })}
          </>
        </Collapse.Panel>
      </Collapse>
    </div>
  );
}

export default RequestLearningSuggestions;
